import { FormGroup } from '@angular/forms';

/**
 * Interface for parsed validation errors
 */
export interface ValidationErrorResult {
  /** Field-specific errors mapped to form control names */
  fieldErrors: { [fieldName: string]: string[] };
  /** General errors that are not field-specific */
  generalErrors: string[];
  /** All errors combined for display */
  allErrors: string[];
}

/**
 * Interface for custom error response format
 */
export interface CustomErrorResponse {
  success: boolean;
  message?: string;
  errors?: string[];
}

/**
 * Interface for ModelState validation response format
 */
export interface ModelStateErrorResponse {
  type?: string;
  title?: string;
  status?: number;
  errors?: { [fieldName: string]: string[] };
  traceId?: string;
}

/**
 * Helper class for processing API validation errors and applying them to reactive forms
 */
export class ValidationErrorHelper {
  /**
   * Parse validation errors from different API response formats
   * @param errorResponse The error response from the API
   * @param customFieldMapping Optional custom field mapping from server names to form control names
   * @returns Parsed validation errors
   */
  static parseValidationErrors(
    errorResponse: any,
    customFieldMapping?: { [serverFieldName: string]: string }
  ): ValidationErrorResult {
    const result: ValidationErrorResult = {
      fieldErrors: {},
      generalErrors: [],
      allErrors: [],
    };

    if (!errorResponse || typeof errorResponse !== 'object') {
      return result;
    }

    // Handle ValidationProblem format: { errors: { "FieldName": ["error1", "error2"] } }
    if (errorResponse.errors && typeof errorResponse.errors === 'object') {
      this.processFieldErrors(errorResponse.errors, result, customFieldMapping);
    }
    // Handle direct ModelState format: { "FieldName": ["error1", "error2"] }
    // Only check this if we haven't already processed ValidationProblem format
    else if (this.isModelStateFormat(errorResponse)) {
      this.processFieldErrors(errorResponse, result, customFieldMapping);
    }

    // Handle custom error format: { success: false, message: "...", errors: ["error1", "error2"] }
    // This can run alongside the above formats for additional processing
    if (errorResponse.success === false) {
      if (errorResponse.message) {
        result.generalErrors.push(errorResponse.message);
        result.allErrors.push(errorResponse.message);
      }

      if (Array.isArray(errorResponse.errors)) {
        result.generalErrors.push(...errorResponse.errors);
        result.allErrors.push(...errorResponse.errors);
      }
    }

    // Handle direct error arrays
    if (Array.isArray(errorResponse)) {
      result.generalErrors.push(...errorResponse);
      result.allErrors.push(...errorResponse);
    }

    // If no structured errors found, try to extract any error message
    if (result.allErrors.length === 0) {
      if (errorResponse.message) {
        result.generalErrors.push(errorResponse.message);
        result.allErrors.push(errorResponse.message);
      } else if (typeof errorResponse === 'string') {
        result.generalErrors.push(errorResponse);
        result.allErrors.push(errorResponse);
      }
    }

    return result;
  }

  /**
   * Apply validation errors to a reactive form
   * @param form The reactive form to apply errors to
   * @param errorResponse The error response from the API
   * @param customFieldMapping Optional custom field mapping from server names to form control names
   * @returns Parsed validation errors (for additional handling if needed)
   */
  static applyValidationErrorsToForm(
    form: FormGroup,
    errorResponse: any,
    customFieldMapping?: { [serverFieldName: string]: string }
  ): ValidationErrorResult {
    const errors = this.parseValidationErrors(errorResponse, customFieldMapping);

    // Clear any existing custom errors first
    this.clearFormValidationErrors(form);

    // Apply field-specific errors to form controls
    for (const fieldName in errors.fieldErrors) {
      const control = form.get(fieldName);
      if (control) {
        const fieldErrors = errors.fieldErrors[fieldName];
        // Set custom error on the control
        control.setErrors({
          ...control.errors,
          serverValidation: fieldErrors,
        });
        // Mark as touched so the error shows
        control.markAsTouched();
      }
    }

    return errors;
  }

  /**
   * Clear server validation errors from form controls
   * @param form The reactive form to clear errors from
   */
  static clearFormValidationErrors(form: FormGroup): void {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (control && control.errors) {
        delete control.errors['serverValidation'];

        // If no other errors remain, set errors to null
        if (Object.keys(control.errors).length === 0) {
          control.setErrors(null);
        }
      }
    });
  }

  /**
   * Get server validation errors for a specific form control
   * @param form The reactive form
   * @param fieldName The field name to get errors for
   * @returns Array of server validation error messages for the field
   */
  static getFieldServerErrors(form: FormGroup, fieldName: string): string[] {
    const control = form.get(fieldName);
    if (control && control.errors && control.errors['serverValidation']) {
      return control.errors['serverValidation'];
    }
    return [];
  }

  /**
   * Check if a form control has server validation errors
   * @param form The reactive form
   * @param fieldName The field name to check
   * @returns True if the field has server validation errors
   */
  static hasFieldServerErrors(form: FormGroup, fieldName: string): boolean {
    const control = form.get(fieldName);
    return !!(control && control.errors && control.errors['serverValidation']);
  }

  /**
   * Map server field names to form control names
   * @param serverFieldName The field name from the server response
   * @param customFieldMapping Optional custom field mapping from server names to form control names
   * @returns The corresponding form control name
   */
  private static mapFieldNameToFormControl(
    serverFieldName: string,
    customFieldMapping?: { [serverFieldName: string]: string }
  ): string {
    // Check custom mapping first if provided
    if (customFieldMapping && customFieldMapping[serverFieldName]) {
      return customFieldMapping[serverFieldName];
    }

    // Common field name mappings from server (PascalCase) to form controls (camelCase)
    const fieldMap: { [key: string]: string } = {
      // Account/Auth related
      Email: 'email',
      Password: 'password',
      NewPassword: 'newPassword',
      OldPassword: 'oldPassword',
      ConfirmPassword: 'confirmPassword',
      FirstName: 'firstName',
      LastName: 'lastName',
      JobTitle: 'jobTitle',
      Department: 'department',
      PhoneNumber: 'phoneNumber',

      // Common form fields
      Username: 'username',
      Name: 'name',
      Title: 'title',
      Description: 'description',

      // Add more mappings as needed based on your forms
    };

    return fieldMap[serverFieldName] || serverFieldName.charAt(0).toLowerCase() + serverFieldName.slice(1);
  }

  /**
   * Get the most appropriate error message for a form field
   * Prioritizes server validation errors over client validation errors
   * @param form The reactive form
   * @param fieldName The field name to get errors for
   * @param defaultClientErrorMessages Optional default messages for client-side validation errors
   * @returns The most appropriate error message or null if no errors
   */
  static getFieldErrorMessage(
    form: FormGroup,
    fieldName: string,
    defaultClientErrorMessages?: { [errorKey: string]: string }
  ): string | null {
    const control = form.get(fieldName);

    if (!control || !control.errors) {
      return null;
    }

    // Check for server validation errors first (highest priority)
    if (control.errors['serverValidation']) {
      const serverErrors = control.errors['serverValidation'];
      return Array.isArray(serverErrors) ? serverErrors[0] : serverErrors;
    }

    // Fall back to client-side validation errors
    if (defaultClientErrorMessages) {
      for (const errorKey in control.errors) {
        if (control.errors.hasOwnProperty(errorKey) && defaultClientErrorMessages[errorKey]) {
          return defaultClientErrorMessages[errorKey];
        }
      }
    }

    // Generic fallback for common validation errors
    if (control.errors['required']) {
      return 'This field is required.';
    }
    if (control.errors['email']) {
      return 'Please enter a valid email address.';
    }
    if (control.errors['minlength']) {
      const requiredLength = control.errors['minlength'].requiredLength;
      return `Minimum length is ${requiredLength} characters.`;
    }
    if (control.errors['maxlength']) {
      const requiredLength = control.errors['maxlength'].requiredLength;
      return `Maximum length is ${requiredLength} characters.`;
    }
    if (control.errors['pattern']) {
      return 'Please enter a valid format.';
    }

    return null;
  }

  /**
   * Get all error messages for a form field (both server and client)
   * @param form The reactive form
   * @param fieldName The field name to get errors for
   * @param defaultClientErrorMessages Optional default messages for client-side validation errors
   * @returns Array of error messages
   */
  static getFieldErrorMessages(
    form: FormGroup,
    fieldName: string,
    defaultClientErrorMessages?: { [errorKey: string]: string }
  ): string[] {
    const control = form.get(fieldName);
    const messages: string[] = [];

    if (!control || !control.errors) {
      return messages;
    }

    // Add server validation errors first
    if (control.errors['serverValidation']) {
      const serverErrors = control.errors['serverValidation'];
      if (Array.isArray(serverErrors)) {
        messages.push(...serverErrors);
      } else {
        messages.push(serverErrors);
      }
    }

    // Add client-side validation errors
    if (defaultClientErrorMessages) {
      for (const errorKey in control.errors) {
        if (
          errorKey !== 'serverValidation' &&
          control.errors.hasOwnProperty(errorKey) &&
          defaultClientErrorMessages[errorKey]
        ) {
          messages.push(defaultClientErrorMessages[errorKey]);
        }
      }
    }

    return messages;
  }

  /**
   * Check if a form field should show error messages
   * @param form The reactive form
   * @param fieldName The field name to check
   * @returns True if errors should be displayed
   */
  static shouldShowFieldErrors(form: FormGroup, fieldName: string): boolean {
    const control = form.get(fieldName);
    if (!control) {
      return false;
    }

    // Always show server validation errors regardless of touched/dirty state
    if (control.errors && control.errors['serverValidation']) {
      return true;
    }

    // For client-side errors, check touched/dirty state
    return !!(control.invalid && (control.dirty || control.touched));
  }

  /**
   * Handle HTTP error responses and extract validation errors
   * @param httpError The HTTP error from Angular HttpClient
   * @returns Parsed validation errors
   */
  static handleHttpError(httpError: any): ValidationErrorResult {
    // Handle different types of HTTP errors
    if (httpError.status === 400 && httpError.error) {
      // Bad Request - likely validation errors
      return this.parseValidationErrors(httpError.error);
    } else if (httpError.status === 401) {
      return {
        fieldErrors: {},
        generalErrors: ['You are not authorized to perform this action.'],
        allErrors: ['You are not authorized to perform this action.'],
      };
    } else if (httpError.status === 403) {
      return {
        fieldErrors: {},
        generalErrors: ['Access denied. You do not have permission to perform this action.'],
        allErrors: ['Access denied. You do not have permission to perform this action.'],
      };
    } else if (httpError.status === 404) {
      return {
        fieldErrors: {},
        generalErrors: ['The requested resource was not found.'],
        allErrors: ['The requested resource was not found.'],
      };
    } else if (httpError.status >= 500) {
      return {
        fieldErrors: {},
        generalErrors: ['A server error occurred. Please try again later.'],
        allErrors: ['A server error occurred. Please try again later.'],
      };
    } else {
      // Try to extract error from the response body
      const errorResponse = httpError.error || httpError.message || 'An unexpected error occurred.';
      return this.parseValidationErrors(errorResponse);
    }
  }

  /**
   * Check if the response is in ModelState format (direct field-to-errors mapping)
   * @param errorResponse The error response to check
   * @returns True if it appears to be ModelState format
   */
  private static isModelStateFormat(errorResponse: any): boolean {
    // ModelState format has field names as keys with error arrays as values
    // and typically doesn't have the standard properties of other formats
    if (!errorResponse || typeof errorResponse !== 'object') {
      return false;
    }

    // If it has an 'errors' property, it's probably ValidationProblem format, not ModelState
    if (errorResponse.errors !== undefined) {
      return false;
    }

    // If it has standard error response properties, it's probably not ModelState
    if (
      errorResponse.success !== undefined ||
      errorResponse.type !== undefined ||
      errorResponse.title !== undefined ||
      errorResponse.status !== undefined ||
      errorResponse.traceId !== undefined ||
      errorResponse.message !== undefined
    ) {
      return false;
    }

    // Check if all properties are arrays (typical ModelState structure)
    const keys = Object.keys(errorResponse);
    if (keys.length === 0) {
      return false;
    }

    // At least one property should be an array for it to be ModelState
    return keys.some(key => Array.isArray(errorResponse[key]));
  }

  /**
   * Process field errors from either ValidationProblem or ModelState format
   * @param fieldsObj The object containing field-to-errors mapping
   * @param result The result object to populate
   * @param customFieldMapping Optional custom field mapping
   */
  private static processFieldErrors(
    fieldsObj: any,
    result: ValidationErrorResult,
    customFieldMapping?: { [serverFieldName: string]: string }
  ): void {
    for (const fieldName in fieldsObj) {
      if (fieldsObj.hasOwnProperty(fieldName) && Array.isArray(fieldsObj[fieldName])) {
        const errors = fieldsObj[fieldName];

        if (fieldName === '' || fieldName === 'general') {
          // General errors (empty key or 'general' key)
          result.generalErrors.push(...errors);
        } else {
          // Field-specific errors
          const mappedFieldName = this.mapFieldNameToFormControl(fieldName, customFieldMapping);
          result.fieldErrors[mappedFieldName] = errors;
        }

        // Add all errors to allErrors for backwards compatibility
        result.allErrors.push(...errors);
      }
    }
  }
}
